import { SearchMapRowPriceOfferContext } from './SearchMapRowPrices'
import type { ComponentProps } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'
import type SearchTile from 'components/SearchKit/SearchTiles/SearchTile'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import SolidStarCircleIcon from 'LuxKit/Icons/solid/SolidStarCircleIcon'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons/BookmarkButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import OfferUrlContext from 'contexts/OfferUrlContext'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { isFlashHotelOffer, isVillaOffer } from 'lib/offer/offerTypes'
import { SearchMapCardCloseContext, SearchMapHoveredOfferContext, SearchMapPriceLayoutContext } from './SearchMapRedesignContext'

const rowImageParams: ImageParams = { mobileWidth: '100px', tabletWidth: '100px', mobileAspectRatio: '1:1', tabletAspectRatio: '1:1', fit: 'center' }
const cardImageParams: ImageParams = { mobileWidth: '375px', tabletWidth: '375px', mobileAspectRatio: '25:12', tabletAspectRatio: '25:12', fit: 'center' }
const Root = styled.article`
  display: grid;
  grid-template-columns: ${rem(100)} minmax(0, 1fr);
  gap: ${rem(16)};
  padding: ${rem(12)};
  border-radius: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  &.hovered, &:hover, &:focus-within {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
  &.map-card {
    display: block;
    cursor: pointer;
    padding: 0;
    width: ${rem(375)};
    max-width: 100%;
    max-height: calc(100dvh - ${rem(144)});
    overflow-y: auto;
    overscroll-behavior: contain;
    box-shadow: ${props => props.theme.shadow.bottom.medium};
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`
const Hero = styled.div`
  position: relative;
  height: ${rem(100)};
  align-self: start;
  overflow: hidden;
  border-radius: ${rem(8)};
  .map-card & { height: ${rem(180)}; border-radius: 0; }
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(4)};
  min-width: 0;
  .map-card & { padding: ${rem(16)}; gap: ${rem(12)}; }
`
const PriceAndRating = styled.div`
  display: flex;
  align-items: center;
  .row & {
    margin-top: auto;
    align-items: flex-end;
    .variant-xs {
      height: ${rem(20)};
      min-width: ${rem(28)};
    }
  }
  justify-content: space-between;
  gap: ${rem(4)};

  .map-card & {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }
`
const RowRating = styled.div`
  margin-inline-start: auto;
  flex-shrink: 0;
`
const Features = styled.div`
  min-width: 0;

`
const Actions = styled.div`
  position: absolute;
  top: ${rem(8)};
  inset-inline-end: ${rem(8)};
  display: flex;
  gap: ${rem(8)};
`
const FlashBadge = styled.span`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  padding: ${rem(8)};
  border-end-end-radius: ${rem(18)};
  background: ${props => props.theme.palette.product.exclusive.background};
  color: ${props => props.theme.palette.neutral.surface.normalBackground};
`
const Labels = styled.div`
  position: absolute;
  top: ${rem(8)};
  inset-inline-start: ${rem(8)};
  max-width: calc(100% - ${rem(100)});
`
const TitleRow = styled.div`
  display: flex;
  gap: ${rem(8)};
  justify-content: space-between;
  align-items: start;
`
const CardTitle = styled(Heading)`
  && { font-size: ${rem(18)}; line-height: ${rem(22)}; font-weight: 600; }
`

type Props = Pick<ComponentProps<typeof SearchTile>, 'offer' | 'title' | 'images' | 'overline' | 'features' | 'pricePoint' | 'action' | 'rating' | 'meta' | 'productLabel' | 'labels' | 'bookmarkAction' | 'eagerLoadFirstImage' | 'onImageLoad' | 'onImageChange' | 'bottomExtension' | 'sponsored'> & { layout: 'row' | 'card' }

/** Shared offer content for the map sidebar row and the card opened from a map pin. */
export default function SearchMapOffer({ offer, title, images, overline, features, pricePoint, action, rating, meta, productLabel, labels, bookmarkAction, eagerLoadFirstImage, onImageLoad, onImageChange, bottomExtension, sponsored, layout }: Props) {
  const isCard = layout === 'card'
  const rowImages = useMemo(() => images.slice(0, 1), [images])
  const intl = useIntl()
  const onClose = useContext(SearchMapCardCloseContext)
  const offerUrl = useContext(OfferUrlContext)
  const dispatch = useContext(OfferListEventsContext)
  const hoveredOfferId = useContext(SearchMapHoveredOfferContext)
  const onLoad = useCallback<NonNullable<Props['onImageLoad']>>((index, image) => {
    dispatch({ type: OfferListEvents.offerReady })
    onImageLoad?.(index, image)
  }, [dispatch, onImageLoad])
  const closeLabel = intl.formatMessage({
    id: 'OXPQ7jxxVF7jgrrzBP2v',
    defaultMessage: 'Close offer preview',
  })
  const sponsoredLabel = intl.formatMessage({
    id: 'G4yqkGqvQQHvE_K5a5bb',
    defaultMessage: 'Sponsored',
  })
  return <Root className={clsx(isCard ? 'map-card' : 'row', { hovered: hoveredOfferId === offer.id })} data-map-offer-id={offer.id} data-map-flash={isFlashHotelOffer(offer) || undefined}>
    <Hero>
      <ImageCarousel
        images={isCard ? images : rowImages}
        imageParams={isCard ? cardImageParams : rowImageParams}
        bottomLeft={<span />}
        arrowBehaviour={isCard ? 'interaction-visible' : 'hidden'}
        eagerLoadFirstImage={eagerLoadFirstImage}
        onImageLoad={onLoad}
        onImageChange={onImageChange}
      />
      {!isCard && isFlashHotelOffer(offer) && <FlashBadge><SolidStarCircleIcon size="S" /></FlashBadge>}
      {isCard && <>
        <Labels>{productLabel}{labels}</Labels>
        <Actions>
          {bookmarkAction && <BookmarkButton offer={offer} withLabel="never" iconKind="primary" iconVariant="ghost" size="medium" />}
          <IconButton kind="primary" variant="ghost" shape="circle" size="medium" onClick={onClose} aria-label={closeLabel}><LineTimesIcon /></IconButton>
        </Actions>
      </>}
    </Hero>
    <Content>
      <TitleRow>
        {isCard ? <ClickableLink to={offerUrl} target="_blank" data-map-offer-link>
          <CardTitle variant="heading5" as="span" lineClamp={2}>{title}</CardTitle>
        </ClickableLink> : <BodyText variant="large" weight="bold" lineClamp={1}>{title}</BodyText>}
        {isCard && rating && <OfferRating variant="xs" rating={rating} onlyNumber />}
      </TitleRow>
      {!isCard && overline}
      {sponsored && <BodyText variant="small">{sponsoredLabel}</BodyText>}
      {(isCard || isFlashHotelOffer(offer) || isVillaOffer(offer)) && <Features>{features}</Features>}
      {isCard && meta}
      <PriceAndRating>
        <SearchMapRowPriceOfferContext.Provider value={!isCard && isFlashHotelOffer(offer) ? offer.id : undefined}>
          <SearchMapPriceLayoutContext.Provider value={isCard ? 'card' : 'row'}>{pricePoint}</SearchMapPriceLayoutContext.Provider>
        </SearchMapRowPriceOfferContext.Provider>
        {!isCard && rating && <RowRating><OfferRating variant="xs" rating={rating} onlyNumber /></RowRating>}
      </PriceAndRating>
      {isCard && action}
      {bottomExtension}
    </Content>
  </Root>
}
